import React from "react";
import { connect } from "react-redux";
import { sendDebugMessage } from "services/debug";
// import { useHistory } from "react-router-dom";

import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonContent,
  IonImg,
  useIonAlert,
} from "@ionic/react";
import AppHeader from "components/Header/AppHeader";
import { useToasts } from "components/Toast/toast";
import Spacer from "components/spacer";

import { WSemit } from "services/socket";
import { nextPage, prevPage, loadQuiz } from "services/quiz";
import { setLoading } from "services/loading";
import { getQuizData, resetQuizData, resetStore } from "services/quiz";
import { logoutUser } from "services/users";
import ChromeCast from "services/chromecast";
import LoadState from "./components/loadState";

const footerImage = "./assets/question-mark-779x600.png";

export interface Props {
  debug: any;
  user: any;
  teams: any;
  quizData: Quiz;
  nextPage: Function;
  prevPage: Function;
  loadQuiz: Function;
  getQuizData: Function;
  setLoading: Function;
  resetStore: Function;
  resetQuizData: Function;
  sendDebugMessage: Function;
  logoutUser: Function;
}

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ["google-cast-launcher"]: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

const Tools: React.FC<Props> = ({
  debug,
  user,
  teams,
  quizData,
  nextPage,
  prevPage,
  loadQuiz,
  getQuizData,
  setLoading,
  resetStore,
  resetQuizData,
  sendDebugMessage,
  logoutUser,
  // ccConnected,
}) => {
  // const history = useHistory();
  const [presentAlert] = useIonAlert();
  const { addToast } = useToasts();

  const { userDetails } = user;
  const { quizId } = quizData;
  const { customerId } = user.userDetails;

  const downloadQuiz = () => {
    resetQuizData();
    setLoading(true, "Loading Quiz Data");
    getQuizData(userDetails.availableQuizId).then(() => {
      addToast({ message: "Quiz data ready", color: "success" });
      setLoading(false);
    });
  };

  const onSendDebug = () => {
    presentAlert({
      header: "Alert",
      subHeader: "Send Debug Data",
      message:
        "This function will send your data set to the Administrator for review. You can continue with your quiz. Do you wish to continue?",
      buttons: [
        {
          text: "OK",
          role: "confirm",
          handler: () => {
            onSendConfirm();
          },
        },
        {
          text: "Cancel",
          role: "cancel",
        },
      ],
    });
  };

  const onSendConfirm = () => {
    let debugData = JSON.parse(JSON.stringify(debug));
    delete debugData.allTeams;

    setLoading(true, "Sending debug data");
    sendDebugMessage(debugData)
      .then(() => {
        addToast("Debug data sent");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onDownloadQuiz = () => {
    if (teams.length > 0 || quizData.progress.round !== 0) {
      presentAlert({
        header: "Alert",
        subHeader: "Redownload Quiz",
        message:
          "You are about to download the quiz. Downloading the quiz can be performed if you want to ensure that you have the latest quiz data. Your quiz data will be retained. Do you wish to continue?",
        buttons: [
          {
            text: "OK",
            role: "confirm",
            handler: () => {
              downloadQuiz();
            },
          },
          {
            text: "Cancel",
            role: "cancel",
          },
        ],
      });
    } else {
      downloadQuiz();
    }
  };

  const confirmResetStore = () => {
    presentAlert({
      header: "Alert",
      subHeader: "Clear Cache",
      message:
        "This action will delete the local cache data and log you out. When you log in, the quiz progress should be reinstated from the database. Do you wish to continue?",
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
        },
        {
          text: "Confirm",
          role: "confirm",
          handler: handleResetStore,
        },
      ],
    });
  };

  const handleResetStore = () => {
    resetStore();
    logoutUser();
  };

  const confirmResetQuiz = () => {
    presentAlert({
      header: "Alert",
      subHeader: "Reset Quiz",
      message:
        "This action will permanently delete the progress and teams data for this quiz. You will be required to start again. Do you wish to continue?",
      buttons: [
        {
          text: "Cancel",
          role: "cancel",
        },
        {
          text: "Confirm",
          role: "confirm",
          handler: handleResetProgress,
        },
      ],
    });
  };

  const handleResetProgress = () => {
    WSemit("clearData", { quizId, customerId });
  };

  return (
    <>
      <AppHeader title="Tools" />

      <IonContent fullscreen className="ion-padding Tools">
        <div className="container">
          <h2>Tools</h2>

          <Spacer size="15" />
          {quizData && quizData.quizId !== 0 && (
            <>
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Data Reset</IonCardTitle>
                  <IonCardSubtitle>
                    These should not be required in normal use. Use with care as
                    the use of these will likely result in data loss.
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonButton onClick={confirmResetStore}>Clear Cache</IonButton>
                  <IonButton onClick={confirmResetQuiz}>Reset Quiz</IonButton>
                </IonCardContent>
              </IonCard>
              <Spacer size="15" />
            </>
          )}

          {quizData && quizData.quizId !== 0 && (
            <>
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle>Redownload</IonCardTitle>
                  <IonCardSubtitle>
                    These should not be required in normal use. Use with care as
                    the use of these will likely result in data loss.
                  </IonCardSubtitle>
                </IonCardHeader>
                <IonCardContent>
                  <IonButton onClick={onDownloadQuiz}>
                    {quizData.quizId === 0
                      ? "Download Quiz"
                      : "Redownload Quiz"}
                  </IonButton>
                </IonCardContent>
              </IonCard>
              <Spacer size="15" />
            </>
          )}

          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Debugging Tools</IonCardTitle>
              <IonCardSubtitle></IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonButton onClick={onSendDebug}>Send Debug Data</IonButton>
            </IonCardContent>
          </IonCard>
          <Spacer size="15" />
          <LoadState />
          <Spacer size="15" />
          {false && (
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Chromecast Test Actions</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <h2>Intro</h2>

                <IonButton onClick={ChromeCast.loadIntro}>Intro</IonButton>
                <IonButton onClick={ChromeCast.loadIntroRounds}>
                  Rounds
                </IonButton>
                <IonButton onClick={ChromeCast.loadIntroPrizes}>
                  Prizes
                </IonButton>
                <IonButton onClick={ChromeCast.loadIntroTeams}>Teams</IonButton>
                <IonButton onClick={ChromeCast.showAds}>Show Ads</IonButton>
                <IonButton onClick={ChromeCast.hideAds}>Hide Ads</IonButton>
                <Spacer size="15" />

                <h2>Teams</h2>

                <IonButton onClick={ChromeCast.updateTestTeams1}>
                  Update Teams 1
                </IonButton>
                <IonButton onClick={ChromeCast.updateTestTeams2}>
                  Update Teams 2
                </IonButton>

                <Spacer size="15" />
                <h2>Quiz</h2>

                <IonButton onClick={ChromeCast.loadTestConfig}>
                  Load Test Config
                </IonButton>
                <IonButton
                  onClick={() =>
                    ChromeCast.loadQuizConfig({ user, quizData, teams })
                  }
                >
                  Load Quiz Config
                </IonButton>
                <IonButton onClick={() => loadQuiz()}>Load Quiz</IonButton>
                <IonButton onClick={() => prevPage()}>Prev</IonButton>
                <IonButton onClick={() => nextPage()}>Next</IonButton>
                <IonButton onClick={ChromeCast.jumpToPuzzle}>Puzzle</IonButton>
                <IonButton onClick={ChromeCast.jumpToBreaker}>
                  Breaker
                </IonButton>
                <Spacer size="15" />

                <h2>Debug</h2>

                <IonButton onClick={ChromeCast.toggleDebug}>
                  Toggle Debug
                </IonButton>
                <IonButton onClick={ChromeCast.refresh}>Refresh</IonButton>
                <IonButton onClick={ChromeCast.reload}>Reload</IonButton>
                <Spacer size="15" />
                <h2>Other</h2>
                <IonButton onClick={ChromeCast.playMusic}>Play Music</IonButton>
                <IonButton onClick={ChromeCast.pauseMusic}>
                  Pause Music
                </IonButton>
                <IonButton onClick={ChromeCast.quit}>Quit</IonButton>
              </IonCardContent>
            </IonCard>
          )}
          <Spacer size="30" />
          <IonImg
            src={footerImage}
            alt="6 subjects"
            style={{ maxWidth: "600px", margin: "0 auto" }}
          />
          <Spacer size="90" />
        </div>
      </IonContent>
    </>
  );
};

const mapStateToProps = (state: any) => ({
  debug: state,
  user: state.user,
  quizData: state.quiz,
  teams: state.teams.teams,
  ccConnected: state.general.ccConnected,
});

export default connect(mapStateToProps, {
  nextPage,
  prevPage,
  loadQuiz,
  getQuizData,
  setLoading,
  resetStore,
  resetQuizData,
  sendDebugMessage,
  logoutUser,
})(Tools);
